import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type {
  BugSeverity,
  BugSource,
  EmployeeSummary,
  PageResponse,
  WorkItemDetail,
} from "../../shared/types/api";
import { BUG_SOURCE_LABELS, SEVERITY_LABELS } from "./workItemVocabulary";

const SEVERITIES: BugSeverity[] = ["BLOCKER", "CRITICAL", "MAJOR", "MINOR", "TRIVIAL"];

const SOURCES: BugSource[] = [
  "DEVELOPMENT",
  "QA",
  "UAT",
  "PRODUCTION",
  "CUSTOMER",
  "INTERNAL_TESTING",
];

/**
 * The defect report (§18).
 *
 * <p>Severity and priority are deliberately separate fields on separate panels. Severity is how
 * broken the product is; priority is when the organisation intends to fix it. A cosmetic fault on
 * the page every customer sees is genuinely minor and genuinely urgent, and a single field would
 * force whoever files it to misrepresent one of the two.
 *
 * <p>Where it was found is worth recording for a reason nothing else captures: the distribution
 * across development, QA and production is the clearest quality signal the organisation has, and
 * §84 rightly forbids inventing a per-person one.
 */
export function BugDetailDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [severity, setSeverity] = useState<BugSeverity>("MAJOR");
  const [bugSource, setBugSource] = useState<BugSource>("DEVELOPMENT");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [environment, setEnvironment] = useState("");
  const [detectedById, setDetectedById] = useState("");
  const [detectedDate, setDetectedDate] = useState("");
  const [affectedVersion, setAffectedVersion] = useState("");
  const [fixVersion, setFixVersion] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [resolution, setResolution] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: Boolean(item),
  });

  useEffect(() => {
    const bug = item?.bug;
    if (!item) {
      return;
    }
    setSeverity(bug?.severity ?? "MAJOR");
    setBugSource(bug?.bugSource ?? "DEVELOPMENT");
    setSteps(bug?.stepsToReproduce ?? "");
    setExpected(bug?.expectedResult ?? "");
    setActual(bug?.actualResult ?? "");
    setEnvironment(bug?.environment ?? "");
    setDetectedById(bug?.detectedById ?? "");
    setDetectedDate(bug?.detectedDate ?? "");
    setAffectedVersion(bug?.affectedVersion ?? "");
    setFixVersion(bug?.fixVersion ?? "");
    setRootCause(bug?.rootCause ?? "");
    setResolution(bug?.resolution ?? "");
    setError(null);
  }, [item]);

  const submit = async () => {
    if (!item) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/work-items/${item.id}/bug-detail`, {
        severity,
        bugSource,
        stepsToReproduce: steps || null,
        expectedResult: expected || null,
        actualResult: actual || null,
        environment: environment || null,
        detectedById: detectedById || null,
        detectedDate: detectedDate || null,
        affectedVersion: affectedVersion || null,
        fixVersion: fixVersion || null,
        rootCause: rootCause || null,
        resolution: resolution || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the report.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="Defect report"
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Save report"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Severity" required hint="How broken it is, not when we will fix it.">
            <Select
              value={severity}
              onChange={(event) => setSeverity(event.target.value as BugSeverity)}
            >
              {SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {SEVERITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Found in" required hint="The distribution across these is a quality signal.">
            <Select
              value={bugSource}
              onChange={(event) => setBugSource(event.target.value as BugSource)}
            >
              {SOURCES.map((value) => (
                <option key={value} value={value}>
                  {BUG_SOURCE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Steps to reproduce">
          <Textarea
            rows={4}
            value={steps}
            onChange={(event) => setSteps(event.target.value)}
            placeholder={"1. Pay with a card that will be declined\n2. Press retry"}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Expected result">
            <Textarea
              rows={2}
              value={expected}
              onChange={(event) => setExpected(event.target.value)}
              placeholder="One charge attempt"
            />
          </Field>
          <Field label="Actual result">
            <Textarea
              rows={2}
              value={actual}
              onChange={(event) => setActual(event.target.value)}
              placeholder="Two charges"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Environment">
            <Input
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
              placeholder="prod-eu"
            />
          </Field>
          <Field label="Affected version">
            <Input
              value={affectedVersion}
              onChange={(event) => setAffectedVersion(event.target.value)}
            />
          </Field>
          <Field label="Fix version">
            <Input
              value={fixVersion}
              onChange={(event) => setFixVersion(event.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Found by">
            <Select
              value={detectedById}
              onChange={(event) => setDetectedById(event.target.value)}
            >
              <option value="">Not recorded</option>
              {(employees.data?.content ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Found on">
            <Input
              type="date"
              value={detectedDate}
              onChange={(event) => setDetectedDate(event.target.value)}
            />
          </Field>
        </div>

        <section className="border-t border-ink-100 pt-4">
          <h3 className="text-sm font-semibold text-ink-900">Once it is fixed</h3>
          <p className="mb-3 mt-0.5 text-sm text-ink-500">
            The pair below is what makes a defect review possible later. Without them a closed bug
            records only that somebody made it go away.
          </p>

          <div className="space-y-4">
            <Field label="Root cause">
              <Textarea
                rows={2}
                value={rootCause}
                onChange={(event) => setRootCause(event.target.value)}
                placeholder="The retry handler did not check for an in-flight authorisation."
              />
            </Field>
            <Field label="Resolution">
              <Textarea
                rows={2}
                value={resolution}
                onChange={(event) => setResolution(event.target.value)}
              />
            </Field>
          </div>
        </section>
      </div>
    </Drawer>
  );
}
